import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, X, Loader2 } from 'lucide-react';
import api from '../../services/api';

interface SearchModelliProps {
  variant?: 'compact' | 'hero';
  placeholder?: string;
}

interface ModelResult {
  id: string;
  nome: string;
  marca: string;
  tipoMacchina: string;
  tipoMacchinaLabel: string;
}

export function SearchModelli({ variant = 'compact', placeholder = 'Ricerca Rapida Modelli' }: SearchModelliProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<ModelResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  // Debounced API search — parte dalla 2a lettera. Guardia anti-race
  // (reqIdRef) aggiunta 26/02/2026: stesso bug/fix di SearchCodici.tsx.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(() => {
      api.get(`/api/abbinatore/autocomplete?q=${encodeURIComponent(q)}&limit=50`)
        .then(r => {
          if (myReqId !== reqIdRef.current) return; // risposta obsoleta, ignorata
          const sorted = (r.data.results || []).slice().sort((a: ModelResult, b: ModelResult) =>
            `${a.marca} ${a.nome}`.localeCompare(`${b.marca} ${b.nome}`, 'it')
          );
          setResults(sorted);
          setSelectedIdx(0);
        })
        .catch(() => { if (myReqId === reqIdRef.current) setResults([]); })
        .finally(() => { if (myReqId === reqIdRef.current) setLoading(false); });
    }, 220);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSelect = useCallback((model: ModelResult) => {
    setQuery('');
    setOpen(false);
    setResults([]);
    navigate(`/consumabili-per/${model.id}`);
  }, [navigate]);

  // Chiudi dropdown click esterno
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[selectedIdx]) {
      e.preventDefault();
      handleSelect(results[selectedIdx]);
    }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  const isHero = variant === 'hero';
  const showDropdown = open && query.trim().length >= 2;

  return (
    <div ref={containerRef} className={`relative ${isHero ? 'flex-1' : 'flex-1 min-w-0'}`}>
      <div className={`flex items-center transition-all duration-150 ${
        isHero
          ? 'rounded-lg border border-gray-300 bg-white shadow-sm h-12 px-4 focus-within:border-2 focus-within:border-[hsl(214,58%,30%)] focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 hover:border-gray-400'
          : 'rounded-lg border border-gray-400 bg-white shadow-sm h-10 px-3 focus-within:border-2 focus-within:border-[hsl(214,58%,30%)] focus-within:ring-2 focus-within:ring-[hsl(214,58%,41%)]/20 hover:border-[hsl(214,58%,41%)]/50'
        }`}
      >
        {loading
          ? <Loader2 strokeWidth={2.4} className={`text-[#006B4F] flex-shrink-0 animate-spin ${isHero ? 'w-4 h-4' : 'w-[18px] h-[18px]'}`} />
          : <Printer strokeWidth={2.4} className={`text-[#006B4F] flex-shrink-0 ${isHero ? 'w-4 h-4' : 'w-[18px] h-[18px]'}`} />
        }
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.trim().length >= 2) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-1 h-full bg-transparent outline-none placeholder:text-gray-500 px-2 ${isHero ? 'text-sm' : 'text-sm font-medium'}`}
          data-testid="search-modelli-input"
          autoComplete="off"
          aria-label="Cerca modello stampante"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false); setResults([]); inputRef.current?.focus(); }}
            className="text-muted-foreground hover:text-foreground flex-shrink-0 ml-1"
            tabIndex={-1}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div
          className="absolute top-full left-0 min-w-[340px] mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-[200] overflow-hidden"
          data-testid="search-modelli-dropdown"
        >
          <div className="max-h-96 overflow-y-auto">
            {results.map((model, idx) => (
              <button
                key={model.id}
                data-testid="search-modelli-result-item"
                onClick={() => handleSelect(model)}
                onMouseEnter={() => setSelectedIdx(idx)}
                className={`w-full text-left px-4 py-1.5 text-[12px] font-medium transition-colors border-b border-gray-100 last:border-0 flex items-center justify-between gap-2 ${
                  idx === selectedIdx
                    ? 'bg-[hsl(214,58%,50%)] text-white'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span>{model.marca} – {model.nome}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showDropdown && !loading && results.length === 0 && (
        <div className="absolute top-full left-0 min-w-[280px] mt-2 bg-white border rounded-xl shadow-2xl z-[200] overflow-hidden">
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-muted-foreground">Nessun modello trovato per "{query}"</p>
            <p className="text-[11px] text-muted-foreground mt-1">Prova con marca + modello, es. "Ricoh 1022"</p>
          </div>
        </div>
      )}
    </div>
  );
}
